import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../Assets/logo.png";
import ApiServices from "../../Services/ApiServices/ApiServices";

const LoginPage = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleTogglePassword = () => setShowPassword((s) => !s);
  const handleCloseSnack = () =>
    setSnack((s) => ({ ...s, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSnack({ open: false, message: "", severity: "info" });

    try {
      const res = await ApiServices.post("auth/login/", {
        identifier: identifier.trim(),
        password,
      });

      const data = res?.data ?? {};
      sessionStorage.setItem("authResponse", JSON.stringify(data));
      sessionStorage.setItem("userType", data?.user_type ?? "");
      if (data?.token) sessionStorage.setItem("authToken", data.token);
      if (data?.user) sessionStorage.setItem("user", JSON.stringify(data.user));

      if (data?.user_type === "employee") navigate("/employee/appointments");
      else if (data?.user_type === "business") navigate("/business/appointments");
      else if (data?.user_type === "customer") navigate("/customer/book-service");
      else
        setSnack({
          open: true,
          message: "Logged in, but unknown user type.",
          severity: "warning",
        });
    } catch (err) {
      let message = "Login failed. Please check credentials.";
      if (err?.response?.data?.detail) message = err.response.data.detail;
      setSnack({ open: true, message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={rootStyles}>
      {/* Local styles */}
      <style>{`
        .login-card {
          width: 420px;
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(9, 76, 159, 0.12);
        }

        .login-card-content {
          padding: 32px 28px 36px;
          display: flex;
          flex-direction: column;
        }

        .logo-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .app-logo {
          width: 115px;
          height: 100px;
          object-fit: contain;
          border-radius: 8px;
        }

        .login-title {
          text-align: center;
          color: #094c9f;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .login-btn {
          margin-top: 14px;
          background-color: #094c9f;
          text-transform: none;
          font-weight: 600;
          padding: 10px;
        }

        .login-btn:hover {
          background-color: #083b82;
        }

        .login-links {
          margin-top: 16px;
          text-align: center;
        }

        .login-link {
          cursor: pointer;
          color: #094c9f;
          font-weight: 500;
        }

        .login-link:hover {
          text-decoration: underline;
        }

        @media (max-width: 480px) {
          .login-card {
            width: 92%;
          }
        }
      `}</style>

      <Card className="login-card">
        <CardContent className="login-card-content">
          {/* Centered Logo */}
          <Box className="logo-wrap">
            <img src={logo} alt="logo" className="app-logo" />
          </Box>

          <Typography variant="h5" className="login-title">
            Sign in
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email or Mobile"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="login-btn"
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} /> : "Login"}
            </Button>
          </form>

          {/* Staff / Manager Login */}
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            className="login-links"
          >
            <Typography
              variant="body2"
              className="login-link"
              onClick={() => navigate("/referal-login")}
            >
              Staff Login
            </Typography>

            <Typography
              variant="body2"
              className="login-link"
              onClick={() => navigate("/admin-login")}
            >
              Manager Login
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={handleCloseSnack}
      >
        <Alert severity={snack.severity} onClose={handleCloseSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;

/* Container styles */
const rootStyles = {
  minHeight: "80vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
  padding: "24px",
};
